import {
  createAutocompletionRequest,
  getAutoCompleteService,
} from 'utils/google-maps/api/placePredictions';
import { AutocompletePredictionPredictionCategory, AutocompletePredictionRestrictionTypes } from 'utils/google-maps/types';
import { getPredictionCategory } from 'utils/google-maps/predictionClassifier';
import { CountryCode, CountryCodeList } from 'types/countries';
import {
  ProvinceAndStateNameMappings,
  countryCodeFromProvinceOrState,
  provinceOrStateCodeFromString,
} from 'utils/province_or_state';
import type { ExtractorOutput } from './types';
import type { MatchSpan, ParsedSearchFilters } from '../types';

const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;
const COUNTRY_TRAILING = /\s*,?\s*(canada|united\s+states|usa|u\.s\.a\.?)\s*$/i;

function escapeRegex(value: string): string {
  return value.replace(REGEX_SPECIAL_CHARS, '\\$&');
}

interface ResolvedLocation {
  city?: string;
  neighbourhood?: string;
  province?: string;
  country?: string;
}

export interface GooglePlacePredictionHit {
  description: string;
  types: string[];
}

export type FetchGooglePlacePredictions = (
  input: string,
  countries: readonly CountryCode[]
) => Promise<GooglePlacePredictionHit[]>;

/**
 * Default Google Places Autocomplete fetch (cities + geocode). No-ops when
 * Maps JS AutocompleteService is unavailable (SSR / missing key).
 */
export async function fetchGooglePlacePredictions(
  input: string,
  countries: readonly CountryCode[]
): Promise<GooglePlacePredictionHit[]> {
  const autocompleteService = await getAutoCompleteService();
  if (!autocompleteService) return [];

  const types = [
    AutocompletePredictionRestrictionTypes.CITIES,
    AutocompletePredictionRestrictionTypes.GEOCODE,
  ];
  const byPlaceId = new Map<string, GooglePlacePredictionHit>();

  for (const type of types) {
    try {
      const request = await createAutocompletionRequest(input, type, countries);
      const response = await autocompleteService.getPlacePredictions(request);
      for (const prediction of response?.predictions ?? []) {
        if (!byPlaceId.has(prediction.place_id)) {
          byPlaceId.set(prediction.place_id, {
            description: prediction.description,
            types: prediction.types ?? [],
          });
        }
      }
    } catch {
      // Ignore per-type failures; try remaining types.
    }
  }

  return [...byPlaceId.values()];
}

function countriesForBias(existing: ParsedSearchFilters): readonly CountryCode[] {
  if (existing.country === CountryCodeList.CANADA) return [CountryCodeList.CANADA];
  if (existing.country === CountryCodeList.UNITED_STATES) {
    return [CountryCodeList.UNITED_STATES];
  }
  if (existing.province) {
    const code = provinceOrStateCodeFromString(existing.province);
    if (code) return [countryCodeFromProvinceOrState(code)];
  }
  return [CountryCodeList.CANADA, CountryCodeList.UNITED_STATES];
}

/**
 * Candidate strings to send to Google, derived from leftover query text.
 * Tries the full string first, then multi-word windows, then single tokens.
 */
export function buildPlaceCandidates(leftover: string): string[] {
  const cleaned = leftover.replace(/\s+/g, ' ').trim();
  if (!cleaned) return [];

  const tokens = cleaned.split(' ').filter(t => t.length >= 4);
  const candidates: string[] = [cleaned];

  for (let size = Math.min(3, tokens.length); size >= 1; size--) {
    for (let i = 0; i + size <= tokens.length; i++) {
      const slice = tokens.slice(i, i + size).join(' ');
      if (!candidates.includes(slice)) candidates.push(slice);
    }
  }

  return candidates;
}

function parseGoogleDescription(
  description: string,
  types: string[]
): ResolvedLocation | null {
  const text = description.replace(COUNTRY_TRAILING, '').trim();
  const parts = text
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;

  let country: string | undefined;
  const last = parts[parts.length - 1];
  const lastAsProvince = provinceOrStateCodeFromString(last);
  if (!lastAsProvince && /^(canada|ca)$/i.test(last)) {
    country = CountryCodeList.CANADA;
    parts.pop();
  } else if (!lastAsProvince && /^(united\s+states|usa|us)$/i.test(last)) {
    country = CountryCodeList.UNITED_STATES;
    parts.pop();
  }

  if (parts.length === 0) return null;

  const provincePart = parts[parts.length - 1];
  const provinceCode = provinceOrStateCodeFromString(provincePart);
  if (!provinceCode) return null;

  const province = provinceCode.toUpperCase();
  country = country ?? countryCodeFromProvinceOrState(provinceCode);

  const category = getPredictionCategory(types);
  const base: ResolvedLocation = { province, country };

  if (parts.length === 1 || category === AutocompletePredictionPredictionCategory.PROVINCE) {
    return base;
  }

  if (category === AutocompletePredictionPredictionCategory.NEIGHBORHOOD && parts.length >= 3) {
    return { ...base, neighbourhood: parts[0], city: parts[1] };
  }

  return { ...base, city: parts[0] };
}

function pushTermSpan(
  spans: MatchSpan[],
  query: string,
  term: string,
  field: string,
  value: unknown,
  ruleId: string
): void {
  if (!term) return;
  const pattern = new RegExp(`\\b${escapeRegex(term).replace(/\s+/g, '\\s+')}\\b`, 'i');
  const match = pattern.exec(query);
  if (!match || match.index === undefined) return;
  spans.push({
    start: match.index,
    end: match.index + match[0].length,
    field,
    raw: query.slice(match.index, match.index + match[0].length),
    value,
    confidence: 0.85,
    ruleId,
  });
}

function buildSpans(
  query: string,
  location: ResolvedLocation,
  matchedCandidate: string
): MatchSpan[] {
  const spans: MatchSpan[] = [];

  // Prefer spanning the leftover token(s) that Google matched.
  if (location.neighbourhood && matchedCandidate.toLowerCase().includes(location.neighbourhood.toLowerCase())) {
    pushTermSpan(spans, query, matchedCandidate, 'neighbourhood', location.neighbourhood, 'google-places.matched');
  } else if (location.city) {
    pushTermSpan(spans, query, matchedCandidate, 'city', location.city, 'google-places.matched');
  } else if (location.province) {
    pushTermSpan(spans, query, matchedCandidate, 'province', location.province, 'google-places.matched');
  }

  if (location.city && matchedCandidate.toLowerCase() !== location.city.toLowerCase()) {
    pushTermSpan(spans, query, location.city, 'city', location.city, 'google-places.city');
  }
  if (location.neighbourhood) {
    pushTermSpan(spans, query, location.neighbourhood, 'neighbourhood', location.neighbourhood, 'google-places.neighbourhood');
  }
  if (location.province) {
    pushTermSpan(spans, query, location.province, 'province', location.province, 'google-places.province-code');
    for (const [name, code] of Object.entries(ProvinceAndStateNameMappings)) {
      if (code.toLowerCase() === location.province.toLowerCase()) {
        pushTermSpan(spans, query, name, 'province', location.province, 'google-places.province-name');
        break;
      }
    }
  }

  return spans;
}

function mergeLocationIntoExisting(
  existing: ParsedSearchFilters,
  resolved: ResolvedLocation
): ResolvedLocation | null {
  const next: ResolvedLocation = {};

  const provinceConflict =
    existing.province &&
    resolved.province &&
    existing.province.toUpperCase() !== resolved.province.toUpperCase();
  if (provinceConflict) return null;

  if (!existing.city && resolved.city) next.city = resolved.city;
  if (!existing.neighbourhood && resolved.neighbourhood) {
    next.neighbourhood = resolved.neighbourhood;
  }
  if (!existing.province && resolved.province) next.province = resolved.province;
  if (!existing.country && resolved.country) next.country = resolved.country;

  // Already had province/country from Go-Search fallback — still allow city fill-in.
  if (existing.province && resolved.city && !existing.city) {
    next.city = resolved.city;
    if (resolved.neighbourhood && !existing.neighbourhood) {
      next.neighbourhood = resolved.neighbourhood;
    }
  }

  return Object.keys(next).length > 0 ? next : null;
}

export interface ExtractGooglePlacesLocationOptions {
  normalizedQuery: string;
  leftover: string;
  existing: ParsedSearchFilters;
  fetchPredictions?: FetchGooglePlacePredictions;
}

/**
 * Use Google Places Autocomplete on leftover tokens to fill missing location
 * fields (gated by `googlePlacePredictions`).
 */
export async function extractGooglePlacesLocation(
  options: ExtractGooglePlacesLocationOptions
): Promise<ExtractorOutput> {
  const {
    normalizedQuery,
    leftover,
    existing,
    fetchPredictions = fetchGooglePlacePredictions,
  } = options;

  const candidates = buildPlaceCandidates(leftover);
  if (candidates.length === 0) return { partial: {}, spans: [] };

  const countries = countriesForBias(existing);

  for (const candidate of candidates) {
    let hits: GooglePlacePredictionHit[];
    try {
      hits = await fetchPredictions(candidate, countries);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('extractGooglePlacesLocation: Google Places failed', error);
      continue;
    }

    for (const hit of hits) {
      const parsed = parseGoogleDescription(hit.description, hit.types);
      if (!parsed) continue;
      // Prefer hits that resolve a city/neighbourhood.
      if (!parsed.city && !parsed.neighbourhood) continue;

      const merged = mergeLocationIntoExisting(existing, parsed);
      if (!merged) continue;

      return {
        partial: merged,
        spans: buildSpans(normalizedQuery, { ...parsed, ...merged }, candidate),
      };
    }
  }

  return { partial: {}, spans: [] };
}
